import type Graph from 'graphology';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { buildFileTree, type FileTreeNode } from '../lib/file-tree-builder.js';

import { FileTreeNodeView } from './file-tree-node.js';

const AUTO_COLLAPSE_DEPTH = 3;

export interface FileTreeProps {
  readonly graph: Graph | null;
  readonly selectedNodeId: string | null;
  readonly onNodeSelect: (nodeId: string) => void;
}

export function FileTree({ graph, selectedNodeId, onNodeSelect }: FileTreeProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const roots = useMemo<FileTreeNode[]>(() => {
    if (!graph) return [];
    return buildFileTree(graph);
  }, [graph]);

  useEffect(() => {
    if (roots.length === 0) return;
    const seed = new Set<string>();
    seedExpanded(roots, 0, seed);
    setExpanded(seed);
  }, [roots]);

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const searchLower = search.trim().toLowerCase();
  const subtreeMatches = (n: FileTreeNode): boolean => {
    if (!searchLower) return true;
    if (n.path.toLowerCase().includes(searchLower)) return true;
    return n.children.some(subtreeMatches);
  };

  if (!graph) return <Empty message="Graph not loaded yet." />;
  if (roots.length === 0) return <Empty message="No directory or file nodes." />;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-border-subtle px-3 py-2">
        <div className="flex items-center gap-2 rounded-lg border border-border-subtle bg-deep px-2 py-1.5 transition-all focus-within:border-accent/40 focus-within:ring-2 focus-within:ring-accent/20">
          <Search className="h-3.5 w-3.5 flex-none text-text-muted" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter files…"
            className="flex-1 border-none bg-transparent text-xs text-text-primary outline-none placeholder:text-text-muted"
          />
        </div>
      </div>
      <div className="scrollbar-thin flex-1 overflow-y-auto py-1">
        {roots.map((root) => (
          <FileTreeNodeView
            key={root.id}
            node={root}
            depth={0}
            expanded={expanded}
            selectedNodeId={selectedNodeId}
            onToggle={toggle}
            onSelect={onNodeSelect}
            isVisible={subtreeMatches}
          />
        ))}
      </div>
    </div>
  );
}

function seedExpanded(nodes: FileTreeNode[], depth: number, out: Set<string>): void {
  for (const n of nodes) {
    if (n.kind !== 'Directory') continue;
    if (depth < AUTO_COLLAPSE_DEPTH) out.add(n.id);
    seedExpanded(n.children, depth + 1, out);
  }
}

function Empty({ message }: { readonly message: string }): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center px-4 py-6 text-center text-xs text-text-muted">
      {message}
    </div>
  );
}
